import { ProductAttributes } from '@app/components/products/ProductAttributes';
import { FilteredProductMatrixSection } from '@app/components/products/ProductMatrix/FilteredProductMatrixSection/FilteredProductMatrixSection';
import { ProductsEtaContext } from '@app/components/products/ProductsEta';
import { RelatedProducts } from '@app/components/products/RelatedProducts';
import { Notification } from '@app/components/various/Notification';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getActiveDeliveryWindowsIdsForOrderBuyer, getFilteredDelwinIds, getIsAnyModalVisible, getOrderDetails } from '@app/ducks';
import { getDefaultImageAspectRatio } from '@app/ducks/helpers';
import { getSortedDeliveryWindowsIds } from '@app/logic/deliveryWindows';
import { getIsOrderEditable, getOrderDelwinIds } from '@app/logic/Orders';
import { getUrlForProductAndVariant, getVariantsForMatrix } from '@app/logic/products';
import { useDelwinIdsWithCurrentStockType, useIsScreenSize } from '@app/utils/hooks';
import { usePdmArrowControls } from '@app/utils/hooks/usePdmKeyboardControls';
import { isDefined } from '@app/utils/is';
import { isEmpty } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { mergeRefs } from 'react-laag';
import { useSelector } from 'react-redux';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import { useProductDetailsLayoutContext } from '../context/ProductDetailsLayoutContext';
import { useProductDetailsModalContext } from '../context/ProductDetailsModalContext';
import { useProductDetailsVariantContext } from '../context/ProductDetailsVariantContext';
import { ProductDetailsBadges } from '../ProductDetailsBadges';
import { ProductDetailsButton } from '../ProductDetailsButton';
import { ProductDetailsDescription } from '../ProductDetailsDescription';
import { ProductDetailsGallery } from '../ProductDetailsGallery';
import { VariantsThumbnailsNavigation } from '../VariantsThumbnailsNavigation';

import { ProductDetailsModalHeader } from './ProductDetailsModalHeader';

const TRANSITION_TIMEOUT = 150;

interface Props {
  onHideModal: () => void;
  scannedBarcode: string | null;
}

export const ProductDetailsModalContent = ({ onHideModal, scannedBarcode }: Props) => {
  const { t } = useTranslation(['products']);
  const orderDetails = useSelector(getOrderDetails);
  const anyModalOpen = useSelector(getIsAnyModalVisible);
  const { activeMediaIndex, activeVariant, medias, productDetails, variants, handleMediaIndexChange } = useProductDetailsVariantContext();
  const { isDesktop } = useProductDetailsLayoutContext();
  const { hasVariantsWithEta } = React.useContext(ProductsEtaContext);
  const [firstVariant] = variants;
  const filteredDelwinIds = useSelector(getFilteredDelwinIds);
  const activeDeliveryWindowsIds = useSelector(getActiveDeliveryWindowsIdsForOrderBuyer);
  const { previousProductUrl, nextProductUrl, isLightboxOpen, setIsLightboxOpen } = useProductDetailsModalContext();

  const sortedOrderDelwinIds = React.useMemo(() => {
    if (!isDefined(firstVariant)) {
      return [];
    }

    const orderDelwinIds = getOrderDelwinIds(variants, orderDetails, firstVariant.product);

    return getSortedDeliveryWindowsIds(orderDelwinIds, activeDeliveryWindowsIds);
  }, [variants, orderDetails, firstVariant, activeDeliveryWindowsIds]);

  const delwinToShowIds = useDelwinIdsWithCurrentStockType(
    !isDefined(filteredDelwinIds) || isEmpty(filteredDelwinIds) ?
      sortedOrderDelwinIds
    : sortedOrderDelwinIds.filter(delwinId => filteredDelwinIds.includes(delwinId)),
  );

  const containerRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  usePdmArrowControls({
    activeMediaIndex,
    enabled: !isLightboxOpen && !anyModalOpen,
    mediasLength: medias.length,
    nextProductUrl,
    onClose: onHideModal,
    onMediaIndexChange: handleMediaIndexChange,
    previousProductUrl,
    scrollRef,
  });

  const filteredVariants = React.useMemo(() => getVariantsForMatrix(variants, productDetails.product), [productDetails.product, variants]);

  const aspectRatio = useSelector(getDefaultImageAspectRatio);
  const isMobile = useIsScreenSize(BREAKPOINTS.LG);

  React.useEffect(() => {
    setTimeout(() => {
      scrollRef.current?.scrollTo(0, 0);
    }, TRANSITION_TIMEOUT);
  }, [productDetails.product]);
  const mobileScrollRef = isDesktop ? undefined : scrollRef;
  const containerMergedRef = mergeRefs(containerRef, mobileScrollRef);

  return (
    <div
      ref={containerMergedRef}
      id="productDetailsModal"
      className="mx-auto h-full w-full max-w-screen-xxl overflow-y-auto bg-white focus:outline-none focus-visible:outline-none lg:flex lg:overflow-hidden lg:bg-transparent"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      <div
        className="sticky top-0 max-h-[calc(100%-5.5rem)] shrink-0 sm:max-h-[calc(100%-10.5rem)] lg:relative lg:max-h-full lg:max-w-[50%]"
        style={{ aspectRatio: isMobile ? undefined : `1 / ${aspectRatio}` }}
      >
        <SwitchTransition>
          <CSSTransition
            key={productDetails.product}
            timeout={TRANSITION_TIMEOUT}
            classNames={{
              enter: 'opacity-0',
              enterActive: '!opacity-100 transition-opacity',
              exit: 'opacity-100',
              exitActive: '!opacity-0 transition-opacity',
            }}
          >
            <ProductDetailsGallery
              productDetails={productDetails}
              medias={medias}
              isLightboxOpen={isLightboxOpen}
              setIsLightboxOpen={setIsLightboxOpen}
            />
          </CSSTransition>
        </SwitchTransition>
      </div>
      <div ref={isDesktop ? scrollRef : undefined} className="contents w-full bg-white lg:block lg:overflow-y-auto">
        <ProductDetailsModalHeader productDetails={productDetails} activeVariant={activeVariant} onHideModal={onHideModal} />
        <div className="relative z-elevated bg-white px-4 pb-5 pt-2 sm:gap-8 sm:pb-24 sm:pt-6 lg:px-8">
          <SwitchTransition>
            <CSSTransition
              key={productDetails.product}
              timeout={TRANSITION_TIMEOUT}
              classNames={{
                enter: 'opacity-0',
                enterActive: '!opacity-100 transition-opacity',
                exit: 'opacity-100',
                exitActive: '!opacity-0 transition-opacity',
              }}
            >
              <div className="flex flex-col gap-4 sm:gap-8">
                <ProductDetailsBadges activeVariant={activeVariant} />
                <ProductAttributes product={activeVariant} />
                <VariantsThumbnailsNavigation
                  productDetails={productDetails}
                  variants={variants}
                  activeVariant={activeVariant}
                  getProductUrl={getUrlForProductAndVariant}
                />
                <div className="w-64">
                  <ProductDetailsButton productDetails={productDetails} deliveryWindowIds={delwinToShowIds} />
                </div>
                <ProductDetailsDescription productDetails={productDetails} activeVariant={activeVariant} />
                {hasVariantsWithEta && <Notification type="info" isBordered message={t('products:variants_availability_info')} />}
                <FilteredProductMatrixSection
                  scannedBarcode={scannedBarcode}
                  productDetails={productDetails}
                  variants={filteredVariants}
                  isReadOnly={!getIsOrderEditable(orderDetails.order.status)}
                  deliveryWindowIds={sortedOrderDelwinIds}
                  deliveryWindowIdsToShow={delwinToShowIds}
                />
                <RelatedProducts />
              </div>
            </CSSTransition>
          </SwitchTransition>
        </div>
      </div>
    </div>
  );
};
